/**
 * Form videos recorded with no signal.
 *
 * The workout page's VideoUploadQueue hands a video here instead of failing it
 * when there is no connection (before the upload, or when the signal drops
 * mid-transfer). The video is stored on the phone as a Blob in IndexedDB with
 * the set it belongs to, and the sync engine uploads it through the normal
 * upload pipeline (upload_video.js) once the connection returns. That pipeline already
 * picks the native background uploader in the Capacitor shell
 * (capacitor/background_upload.js), so a queued video keeps uploading if the
 * client locks the phone on the way out of the gym.
 *
 * NO DUPLICATES. Each video gets one client_upload_key when it is saved. Every
 * attempt sends it, and POST /api/v1/videos answers a key it has seen with the
 * existing video. A video therefore lands on its set once, however many times
 * a lost reply makes us retry it.
 */

import { VIDEOS_STORE, deleteRecord, getAll, putRecord } from "./offline_db";
import { mintIdempotencyKey } from "./set_save_queue";

export const MAX_VIDEO_ATTEMPTS = 5;
export const VIDEO_STATUS_PENDING = "pending";
export const VIDEO_STATUS_FAILED = "failed";

export class OfflineStorageFullError extends Error {
  constructor() {
    super(
      "There isn't enough free space on this phone to save the video until you're back online.",
    );
    this.name = "OfflineStorageFullError";
  }
}

const currentUserId = () =>
  typeof window !== "undefined" && window._tkcCurrentUserId != null
    ? String(window._tkcCurrentUserId)
    : null;

let persistRequested = false;

// Ask the browser not to evict our data under storage pressure. Best effort:
// unsupported or refused simply means default (usually generous) retention.
const requestPersistentStorage = () => {
  if (persistRequested) return;
  persistRequested = true;
  try {
    navigator.storage?.persist?.()?.catch?.(() => {});
  } catch {
    // ignore
  }
};

// Refuse up front rather than half-write a 300 MB clip and fail at the end.
const assertSpaceFor = async (bytes) => {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (!estimate || !Number.isFinite(estimate.quota)) return;
    const free = estimate.quota - (estimate.usage || 0);
    if (free < bytes * 1.1) throw new OfflineStorageFullError();
  } catch (error) {
    if (error instanceof OfflineStorageFullError) throw error;
    // No estimate available: let the write itself decide.
  }
};

/**
 * Save a picked/recorded video for upload later. `target` is where it goes:
 * { assignedSetId, setIndex, assignedExerciseId, workoutId }.
 * Resolves the stored record (without the blob).
 */
export async function saveOfflineVideo({
  file,
  uploadFilename,
  name,
  target,
  clientUploadKey = null,
}) {
  const userId = currentUserId();
  if (!userId || !file) {
    throw new Error("Cannot save a video offline without a user and a file");
  }
  await assertSpaceFor(file.size || 0);
  requestPersistentStorage();

  const record = {
    id: mintIdempotencyKey("vid"),
    // The id doubles as the server dedupe key; kept separately so the two can
    // diverge later without a migration of stored records.
    clientUploadKey: null,
    userId,
    blob: file,
    fileName: file.name || "video.mp4",
    fileType: file.type || "",
    lastModified: file.lastModified || Date.now(),
    size: file.size || 0,
    uploadFilename: uploadFilename || file.name || null,
    name: name || file.name || "video",
    assignedSetId: target?.assignedSetId ?? null,
    setIndex: target?.setIndex ?? null,
    assignedExerciseId: target?.assignedExerciseId ?? null,
    workoutId: target?.workoutId ?? null,
    status: VIDEO_STATUS_PENDING,
    attempts: 0,
    lastError: null,
    createdAt: Date.now(),
  };
  // Reuse the key an in-page attempt already sent: if that attempt created the
  // video and only the reply was lost, the server must recognise this upload
  // as the same one.
  record.clientUploadKey = clientUploadKey || record.id;

  try {
    await putRecord(VIDEOS_STORE, record);
  } catch (error) {
    if (error?.name === "QuotaExceededError")
      throw new OfflineStorageFullError();
    throw error;
  }
  return withoutBlob(record);
}

/** A record without its Blob, safe to hand to the page or keep in memory. */
export const withoutBlob = (record) => {
  const summary = { ...record };
  delete summary.blob;
  return summary;
};

/** Every stored video, oldest first. */
export async function loadOfflineVideos() {
  const records = await getAll(VIDEOS_STORE);
  return records.sort((a, b) => a.createdAt - b.createdAt);
}

/** Rebuild the File the upload pipeline takes from a stored record. */
export const fileFromRecord = (record) =>
  new File([record.blob], record.fileName, {
    type: record.fileType,
    lastModified: record.lastModified,
  });

export const removeOfflineVideo = (id) => deleteRecord(VIDEOS_STORE, id);

export const updateOfflineVideo = (record, patch) =>
  putRecord(VIDEOS_STORE, { ...record, ...patch });

export const resetOfflineVideoQueueForTests = () => {
  persistRequested = false;
};
